import { collection, collectionGroup, deleteDoc, doc, getDoc, serverTimestamp, setDoc, Timestamp, updateDoc, } from "firebase/firestore";
import { warnUnknownFields } from "./converterWarnings";
import { prepareForFirestore } from "./utils";
const USER_FIELDS = [
    "id",
    "path",
    "companyId",
    "email",
    "displayName",
    "role",
    "phoneNumber",
    "lastLoginAt",
    "lastLogin",
    "createdAt",
    "internalAuth",
];
export const userConverter = {
    toFirestore(value) {
        return prepareForFirestore(value);
    },
    fromFirestore(snapshot, options) {
        const data = snapshot.data(options);
        warnUnknownFields("packages/ui User.fromFirestore", data, USER_FIELDS);
        // Active field is `lastLoginAt` (standardized on `...At` suffix).
        // Legacy documents used `lastLogin`; we check `lastLoginAt` first, falling back to `lastLogin`.
        const lastLoginDate = data.lastLoginAt instanceof Timestamp
            ? data.lastLoginAt?.toDate()
            : data.lastLogin instanceof Timestamp
                ? data.lastLogin?.toDate()
                : null;
        return {
            id: snapshot.id,
            path: snapshot.ref.path,
            companyId: snapshot.ref.parent.parent?.id || data.companyId,
            email: data.email,
            displayName: data.displayName || "",
            role: data.role || "user",
            phoneNumber: data.phoneNumber,
            lastLoginAt: lastLoginDate,
            // Deprecated alias maintained for backwards compatibility with legacy UI components reading `user.lastLogin`
            lastLogin: lastLoginDate,
            internalAuth: {
                configured: data.internalAuth?.configured === true ||
                    (typeof data.internalAuth?.phraseHash === "string" &&
                        typeof data.internalAuth?.phraseSalt === "string"),
                updatedAt: data.internalAuth?.updatedAt instanceof Timestamp
                    ? data.internalAuth.updatedAt.toDate()
                    : data.internalAuth?.phraseResetAt instanceof Timestamp
                        ? data.internalAuth.phraseResetAt.toDate()
                        : data.internalAuth?.phraseSetAt instanceof Timestamp
                            ? data.internalAuth.phraseSetAt.toDate()
                            : null,
            },
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt?.toDate() : null,
        };
    },
};
export function usersCollection(db, companyId) {
    return collection(db, "companies", companyId, "users").withConverter(userConverter);
}
export function allUsersQuery(db) {
    return collectionGroup(db, "users").withConverter(userConverter);
}
export function userDocument(db, companyId, userId) {
    return doc(db, "companies", companyId, "users", userId).withConverter(userConverter);
}
export async function fetchUser(db, companyId, userId) {
    const snapshot = await getDoc(userDocument(db, companyId, userId));
    return snapshot.exists() ? snapshot.data() : null;
}
export async function updateUser(db, companyId, userId, data) {
    const docRef = userDocument(db, companyId, userId);
    await setDoc(docRef, data, { merge: true });
}
export async function deleteUser(db, companyId, userId) {
    await deleteDoc(userDocument(db, companyId, userId));
}
export async function touchUser(db, uid, user) {
    return updateDoc(userDocument(db, user.companyId, uid), {
        lastLoginAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
    });
}
